import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AssetIcon,
  AssetPreview,
  Badge,
  Button,
  Card,
  ChannelFormatPreview,
  Cluster,
  CreativeIcon,
  DocumentIcon,
  EmptyState,
  EyeIcon,
  Field,
  Grid,
  LayersIcon,
  Metric,
  Page,
  Palette,
  PaletteIcon,
  QueryState,
  Select,
  SparkleIcon,
  Stack,
  StatStrip,
  TagIcon,
  cx,
} from '@integra/ui';
import {
  isNotFound,
  useAssetPreview,
  useAssets,
  useBrandSystem,
  useRenderAsset,
  useTemplates,
} from '../api/hooks.js';

/**
 * The creative engine surface (§20.3): the Brand System, the Templates that
 * reference it, and the template preview an operator uses before committing a
 * render to a campaign.
 *
 * All data access is through the hooks layer (§21.4) — this component performs
 * no HTTP of its own, including for the rendered image bytes.
 */

/**
 * Asset `data` is an open bag (§6.1); read a field only when it really is the
 * type we need rather than coercing whatever happens to be there.
 */
function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function formatBytes(bytes: number): string {
  return bytes < 1_000_000
    ? `${(bytes / 1_000).toFixed(1)} kB`
    : `${(bytes / 1_000_000).toFixed(2)} MB`;
}

/** One segmented tab on the Creative studio. */
function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors duration-fast',
        active
          ? 'border-navy-700 bg-navy-700 text-ink-inverse'
          : 'border-edge text-navy-700 hover:border-navy-400',
      )}
    >
      {label}
    </button>
  );
}

export function CreativePage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();

  const brand = useBrandSystem(workspaceId);
  const templates = useTemplates(workspaceId);
  // Only product Assets carry the fields a template binds to, so the picker is
  // filtered rather than offering a render that would bind nothing.
  const products = useAssets(workspaceId, { type: 'product' });
  const render = useRenderAsset(workspaceId);

  const [assetId, setAssetId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [tab, setTab] = useState<'brand' | 'templates' | 'preview'>('brand');

  const result = render.data;
  // The preview endpoint is workspace-authorised, so the bytes arrive as a Blob
  // through the hooks layer and are exposed as an object URL (see
  // `useAssetPreview`), never as a bare URL an <img> could request unauthenticated.
  const preview = useAssetPreview(workspaceId, result?.asset.id ?? '');

  // A 404 here means "no Brand System configured yet", which is a normal state
  // for a new workspace — not a failure worth an error banner (§9.1).
  const brandMissing = isNotFound(brand.error);
  const brandError = brandMissing ? null : brand.error;

  const templateList = templates.data?.data ?? [];
  const productList = products.data?.data ?? [];
  const canRender = assetId !== '' && templateId !== '';

  return (
    <Page
      title="Creative studio"
      icon={CreativeIcon}
      description="Set your brand once and every template resolves against it, so one change re-skins every render."
      stats={
        templateList.length > 0 || productList.length > 0 ? (
          <StatStrip
            stats={[
              { icon: LayersIcon, label: 'Templates', value: templateList.length, tone: 'accent' },
              { icon: AssetIcon, label: 'Products', value: productList.length },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Cluster gap="sm">
          <TabButton
            active={tab === 'brand'}
            onClick={() => setTab('brand')}
            label="Brand system"
          />
          <TabButton
            active={tab === 'templates'}
            onClick={() => setTab('templates')}
            label={`Templates (${String(templateList.length)})`}
          />
          <TabButton
            active={tab === 'preview'}
            onClick={() => setTab('preview')}
            label="Preview render"
          />
        </Cluster>

        {tab === 'brand' && (
          <Card
            title="Brand system"
            subtitle="One source of truth. A template asks for @primary or @heading and the renderer resolves it here — change a colour, every render re-skins on its own."
          >
            <QueryState loading={brand.isLoading} error={brandError} what="the brand system">
              {brand.data === undefined ? (
                <EmptyState
                  icon={PaletteIcon}
                  title="No brand system configured yet"
                  description="Until one is set, templates have no tokens to resolve and a render will be rejected. Configure colours, typography and spacing for this workspace to enable rendering."
                  features={[
                    { icon: PaletteIcon, label: 'Colour tokens every render resolves' },
                    { icon: DocumentIcon, label: 'Typography roles like @heading & @body' },
                    { icon: SparkleIcon, label: 'Change once, re-skin every render' },
                  ]}
                />
              ) : (
                <Stack gap="lg">
                  <div className="rounded-md border border-edge bg-surface-sunken px-4 py-4">
                    <p className="mb-3 text-xs uppercase tracking-wide text-ink-muted">
                      Palette at a glance
                    </p>
                    <Palette
                      colours={Object.entries(brand.data.colours).map(([name, value]) => ({
                        name: `@${name}`,
                        value,
                      }))}
                    />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-ink-muted">Colour tokens</p>
                    <Grid min="sm" gap="sm" className="mt-2">
                      {Object.entries(brand.data.colours).map(([token, hex]) => (
                        <div
                          key={token}
                          className="flex items-center gap-3 rounded-md border border-edge bg-surface px-3 py-2"
                        >
                          {/*
                           * The swatch colour is workspace DATA, not a design
                           * decision, so it cannot be a token utility class. It is
                           * painted as an SVG fill attribute rather than an inline
                           * style, which keeps §21.4's ban on inline styling intact.
                           */}
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            role="img"
                            aria-label={`${token} swatch`}
                            className="shrink-0 rounded-sm border border-edge-strong"
                          >
                            <rect width="32" height="32" fill={hex} />
                          </svg>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-navy-700">@{token}</p>
                            <p className="font-mono text-xs uppercase text-ink-muted">{hex}</p>
                          </div>
                        </div>
                      ))}
                    </Grid>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide text-ink-muted">Typography</p>
                    <Grid min="sm" gap="sm" className="mt-2">
                      {Object.entries(brand.data.typography).map(([role, spec]) => (
                        <div
                          key={role}
                          className="rounded-md border border-edge bg-surface px-3 py-2"
                        >
                          <p className="text-sm font-semibold text-navy-700">@{role}</p>
                          <p className="font-mono text-xs text-ink-muted">{spec.family}</p>
                          {spec.fallbacks.length > 0 && (
                            <p className="truncate text-xs text-ink-muted">
                              falls back to {spec.fallbacks.join(', ')}
                            </p>
                          )}
                          {spec.weight !== undefined && (
                            <p className="mt-1 text-xs text-ink-muted">weight {spec.weight}</p>
                          )}
                        </div>
                      ))}
                    </Grid>
                  </div>
                </Stack>
              )}
            </QueryState>
          </Card>
        )}

        {tab === 'templates' && (
          <Card
            title="Templates"
            subtitle="Each template is a canvas plus elements bound to Asset fields and brand tokens."
          >
            <QueryState loading={templates.isLoading} error={templates.error} what="templates">
              {templateList.length === 0 ? (
                <EmptyState
                  icon={LayersIcon}
                  title="No templates yet"
                  description="A template defines the canvas and the elements a render draws. Without one there is nothing to preview."
                  features={[
                    { icon: LayersIcon, label: 'A canvas plus positioned elements' },
                    { icon: TagIcon, label: 'Elements bound to Asset fields & tokens' },
                    { icon: EyeIcon, label: 'Preview before committing a render' },
                  ]}
                />
              ) : (
                <Grid min="md" gap="md">
                  {templateList.map((template) => (
                    <Stack key={template.id} gap="sm">
                      <ChannelFormatPreview
                        channel={template.name}
                        width={template.layout.canvas.width}
                        height={template.layout.canvas.height}
                      />
                      <Cluster gap="sm">
                        <Badge>{template.templateType}</Badge>
                        <span className="font-mono text-[11px] text-ink-muted">{template.id}</span>
                      </Cluster>
                    </Stack>
                  ))}
                </Grid>
              )}
            </QueryState>
          </Card>
        )}

        {tab === 'preview' && (
          <Card
            title="Template preview"
            subtitle="Render a source Asset through a template and see exactly what would be published."
          >
            <Stack gap="md">
              <QueryState
                loading={products.isLoading || templates.isLoading}
                error={products.error ?? templates.error}
                what="the preview inputs"
              >
                {productList.length === 0 || templateList.length === 0 ? (
                  <EmptyState
                    icon={EyeIcon}
                    title="Not enough to preview yet"
                    description={
                      productList.length === 0
                        ? 'There are no product Assets in this workspace to render. Create one, or sync a catalogue in.'
                        : 'There are no templates in this workspace to render through.'
                    }
                    features={[
                      { icon: AssetIcon, label: 'Render a product Asset through a template' },
                      { icon: PaletteIcon, label: 'Brand tokens resolved into the output' },
                      { icon: EyeIcon, label: 'See exactly what would be published' },
                    ]}
                  />
                ) : (
                  <Cluster gap="md" align="end">
                    <Field label="Source asset" htmlFor="render-asset">
                      <Select
                        id="render-asset"
                        value={assetId}
                        onChange={(e) => setAssetId(e.target.value)}
                      >
                        <option value="">Choose an asset…</option>
                        {productList.map((asset) => (
                          <option key={asset.id} value={asset.id}>
                            {readString(asset.data, 'title') ?? asset.id}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Template" htmlFor="render-template">
                      <Select
                        id="render-template"
                        value={templateId}
                        onChange={(e) => setTemplateId(e.target.value)}
                      >
                        <option value="">Choose a template…</option>
                        {templateList.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name} ({template.layout.canvas.width}×
                            {template.layout.canvas.height})
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Button
                      onClick={() => render.mutate({ assetId, templateId })}
                      loading={render.isPending}
                      disabled={!canRender}
                    >
                      Render preview
                    </Button>
                  </Cluster>
                )}
              </QueryState>

              {render.error instanceof Error && (
                <Alert tone="error" title="Render failed">
                  {render.error.message}
                </Alert>
              )}

              {result !== undefined && (
                <Grid min="md" gap="md">
                  <Stack gap="sm">
                    <AssetPreview
                      title={`Rendered ${String(result.width)}×${String(result.height)}`}
                      mediaUrl={preview.url}
                      type={result.asset.type}
                      state={result.asset.state}
                    />
                    {preview.error instanceof Error && (
                      <Alert tone="warning" title="Rendered, but the image could not be loaded">
                        {preview.error.message}
                      </Alert>
                    )}
                  </Stack>
                  <Stack gap="sm">
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="Width" value={result.width} unit="px" />
                      <Metric label="Height" value={result.height} unit="px" />
                    </div>
                    <Metric
                      label="File size"
                      value={formatBytes(result.byteSize)}
                      caption="What the channel will receive; format specs cap this per destination."
                    />
                    <p className="font-mono text-[11px] text-ink-muted">media {result.mediaRef}</p>
                  </Stack>
                </Grid>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Page>
  );
}
